//@flow
import { useCallback, useMemo, useContext, useState } from "react";
import { ArrowNavigationContext } from "data/context";
import { getRoute } from "components/ItemUriLink";
import { produce } from "immer";
import { type ArrowNavigationSetter, type PaginationProps } from "data/types";
import { DEFAULT_ARROWNAVIGATION_CONTEXT } from "./utils";

export const useSearchPagination = ({
    history,
    currentRow,
    setCurrentRow,
    multiple,
    activeRows,
    isMini,
}): PaginationProps => {
    const { setTarget, setCurrentIndex, setRows }: ArrowNavigationSetter =
        useContext(ArrowNavigationContext) || DEFAULT_ARROWNAVIGATION_CONTEXT;

    const setCurrent = useCallback(
        (index, row) => {
            setCurrentIndex(index);
            setCurrentRow(index);
            if (row == null) return index;
            !row?.isDoc &&
                !isMini &&
                history.push(
                    getRoute({
                        itemUri: row.itemUri,
                        isDoc: row.isDoc,
                        isMini,
                    })
                );
            return index;
        },
        [setCurrentIndex, setCurrentRow, history, isMini]
    );

    const [totalCount, setTotalCount] = useState({});
    const [documents, setDocuments] = useState({});

    const getDocument = useCallback(
        (index: number): ?SearchResultRowDTO => {
            const row = Object.prototype.hasOwnProperty.call(documents, index);
            // Return only documents from multi selection
            if (multiple && !activeRows?.hasOwnProperty(index)) {
                return undefined;
            }
            if (!row) {
                return undefined;
            }
            return documents[index];
        },
        [activeRows, documents, multiple]
    );

    const getActiveRowItemUri = useCallback(
        (index: number): ?string => {
            const row = getDocument(index);
            return row?.itemUri;
        },
        [getDocument]
    );

    const getNextDocumentIndex = useCallback(
        (index) => {
            for (let i = index; i < totalCount; ++i) {
                const row = getDocument(i);
                /* istanbul ignore else */
                if (typeof row !== "undefined") {
                    return i;
                }
            }
            return index - 1;
        },
        [totalCount, getDocument]
    );

    const getPrevDocumentIndex = useCallback(
        (index: number): number => {
            for (let i = index; i >= 0; --i) {
                const row = getDocument(i);
                /* istanbul ignore else */
                if (typeof row !== "undefined") {
                    return i;
                }
            }
            return index + 1;
        },
        [getDocument]
    );

    const handleRowsUpdate = useCallback(
        (documents, totalCount) => {
            const filteredRows = {};
            Object.keys(documents).forEach((i) => {
                const index = parseInt(i, 10);
                if (
                    !multiple ||
                    Object.keys(activeRows ?? {}).length === 0 ||
                    activeRows?.hasOwnProperty(i)
                ) {
                    filteredRows[index] = documents[i]?.itemUri;
                }
            });
            setRows({
                ...filteredRows,
                length: totalCount,
            });
        },
        [multiple, activeRows, setRows]
    );

    const handleRowsLoaded = useCallback(
        (rows, totalCount) => {
            if (totalCount === 0) {
                setTotalCount(0);
                setDocuments({});
                return;
            }
            const nextDocuments = produce(documents, (draft) => {
                for (let index in rows) {
                    if (!rows[index]?.isDoc) continue;
                    draft[index] = rows[index];
                }
            });
            setDocuments(nextDocuments);
            setTotalCount(totalCount);
            handleRowsUpdate(nextDocuments, totalCount);
        },
        [documents, handleRowsUpdate]
    );

    const onSetActiveRowIndex = useCallback(
        (index: number, isNext: boolean): index => {
            const documentIndex = isNext
                ? getNextDocumentIndex(index)
                : getPrevDocumentIndex(index);
            return setCurrent(documentIndex, getDocument(documentIndex));
        },
        [getNextDocumentIndex, getPrevDocumentIndex, setCurrent, getDocument]
    );

    const getProps = useCallback(
        (activeRowIndex) => {
            handleRowsUpdate(documents, totalCount);
            return {
                activeRowIndex,
                totalCount,
                onSetActiveRowIndex,
                getActiveRowItemUri,
                setTarget,
            };
        },
        [
            handleRowsUpdate,
            getActiveRowItemUri,
            setTarget,
            onSetActiveRowIndex,
            documents,
            totalCount,
        ]
    );

    return useMemo(
        () => ({
            getProps,
            setCurrent,
            handleRowsUpdate: () => handleRowsUpdate(documents, totalCount),
            handleRowsLoaded,
        }),
        [
            documents,
            totalCount,
            setCurrent,
            getProps,
            handleRowsUpdate,
            handleRowsLoaded,
        ]
    );
};
